import { useEffect, useState } from 'react'
import {
  cancelarEnvio,
  descartarEnvio,
  reenviar,
  SEGUNDOS_PARA_CANCELAR,
  useEnviosPendentes,
  type CitacaoDoEnvio,
  type EnvioPendente,
} from '../../envio/fila-de-envio'

/**
 * Os cartões que ficam acima do campo de mensagem enquanto ela não saiu.
 *
 * Vermelho de propósito, e só aqui. O cartão existe por 5 segundos e pede uma
 * decisão rápida — "era isso mesmo?" —, que é o caso em que cor forte ajuda em
 * vez de cansar. Some sozinho quando a mensagem sai.
 */
export function EnviosPendentes({
  chamadoId,
  aoDevolverTexto,
}: {
  chamadoId: string
  /** Devolve o texto ao campo, para quem cancelou poder corrigir. */
  aoDevolverTexto: (texto: string, respondendoA: CitacaoDoEnvio | null) => void
}) {
  const envios = useEnviosPendentes(chamadoId)
  if (envios.length === 0) return null

  return (
    /*
      Faixas de ponta a ponta, coladas na caixa de escrever: fazem parte da área de escrever, e
      cartão arredondado com margem parecia coisa solta no meio da conversa.
    */
    <div aria-live="polite">
      {envios.map((envio) => (
        <CartaoDeEnvio key={envio.id} envio={envio} aoDevolverTexto={aoDevolverTexto} />
      ))}
    </div>
  )
}

function CartaoDeEnvio({
  envio,
  aoDevolverTexto,
}: {
  envio: EnvioPendente
  aoDevolverTexto: (texto: string, respondendoA: CitacaoDoEnvio | null) => void
}) {
  const [agora, setAgora] = useState(() => Date.now())

  // O relógio só anda enquanto dá para cancelar; enviando ou com falha, parado.
  useEffect(() => {
    if (envio.estado !== 'aguardando') return
    const relogio = window.setInterval(() => setAgora(Date.now()), 100)
    return () => window.clearInterval(relogio)
  }, [envio.estado])

  const restante = Math.max(0, envio.saiEm - agora)
  const segundos = Math.ceil(restante / 1000)
  const fracao = Math.min(1, restante / (SEGUNDOS_PARA_CANCELAR * 1000))

  if (envio.estado === 'falhou') {
    return (
      <div className="border-t border-red-300 bg-red-50 px-4 py-2.5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-red-800">A mensagem não foi enviada.</p>
            <p className="text-xs text-red-700">{envio.erro}</p>
            <p className="mt-1 truncate text-xs text-red-900/70">“{envio.conteudo}”</p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={() => reenviar(envio.id)}
              className="rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700"
            >
              Tentar de novo
            </button>
            {/*
              "Editar" devolve o texto ao campo e tira o cartão. Sem ele, a
              única forma de corrigir uma mensagem que falhou seria redigitar.
            */}
            <button
              type="button"
              onClick={() => {
                aoDevolverTexto(envio.conteudo, envio.respondendoA)
                descartarEnvio(envio.id)
              }}
              className="rounded-full bg-superficie px-3 py-1.5 text-xs font-semibold text-red-700 ring-1 ring-red-300 transition hover:bg-red-50"
            >
              Editar
            </button>
          </div>
        </div>
      </div>
    )
  }

  const enviando = envio.estado === 'enviando'

  return (
    <div className="border-t border-red-300 bg-red-50">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-red-800">
            {enviando
              ? 'Enviando…'
              : `Cometeu um erro? Você pode cancelar dentro de ${segundos} segundo${segundos === 1 ? '' : 's'}.`}
          </p>
          {/* O texto aparece para a pessoa conferir sem precisar rolar a conversa. */}
          <p className="truncate text-xs text-red-900/70">“{envio.conteudo}”</p>
        </div>

        {!enviando ? (
          <button
            type="button"
            onClick={() => {
              const cancelado = cancelarEnvio(envio.id)
              if (cancelado) aoDevolverTexto(cancelado.conteudo, cancelado.respondendoA)
            }}
            className="shrink-0 rounded-md bg-red-600 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-red-700 active:scale-[0.98]"
          >
            Cancelar
          </button>
        ) : null}
      </div>

      {/*
        A barra que encolhe diz quanto falta sem precisar ler o número. O
        número sozinho pula de 5 para 4 de uma vez; a barra mostra que o tempo
        está correndo entre um e outro.
      */}
      <div className="h-1 bg-red-100">
        <div
          className="h-full bg-red-500 transition-[width] duration-100 ease-linear"
          style={{ width: `${enviando ? 0 : fracao * 100}%` }}
        />
      </div>
    </div>
  )
}
